import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';
import styles from './Select.module.css'
import cx from "classnames";
import Loader from "../Loader";

export type SelectOption = {
    value: string;
    label: string;
};

type SelectProps = {
    options: SelectOption[];
    onSelect: (property: string, value: string) => void
    property: string
    value: string
    placeholder: string
    classes?: string
    searchable?: boolean
    onSearchChange?: (search: string) => void
    loading?: boolean
    searchTermUpdate?: boolean
};

const Select: React.FC<SelectProps> = ({
    options,
    onSelect,
    property,
    value,
    placeholder,
    classes,
    searchable= true,
    onSearchChange,
    loading = false,
    searchTermUpdate = true
}) => {
    const [searchTerm, setSearchTerm] = useState(options.find((o) => o.value === value)?.label ?? '');
    const [isDropdownOpen, setDropdownOpen] = useState(false);

    const filteredOptions = searchable
        ? options.filter((option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        ) : options;

    const handleSelectOption = useCallback((option: SelectOption) => {
        if (option.value !== value) onSelect(property, option.value)
        if (searchTermUpdate) setSearchTerm(option.label);
        setDropdownOpen(false);
    }, [onSelect, property, value, searchTermUpdate]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const found = options.find((opt) => {
            return opt.label.toLowerCase() === e.target.value.toLowerCase();
        });

        if (found) {
            onSelect(property, found.value);
        } else if (value !== '') {
            onSelect(property, '');
        }

        if (searchTermUpdate) setSearchTerm(e.target.value);
    };

    const handleClearSelection = () => {
        onSelect(property, '');
        setSearchTerm('');
    };

    useEffect(() => {
        if (onSearchChange) onSearchChange(searchTerm)
    }, [searchTerm, onSearchChange])

    useEffect(() => {
        if (searchTermUpdate && searchTerm === '') {
            setSearchTerm(options.find((o) => String(o.value) === String(value))?.label ?? '');
        }
    }, [value, searchTerm, searchTermUpdate, options])

    return (
        <div className={cx(styles['zr-select-wrapper'], classes)}>
            <input
                type="text"
                className={cx(styles['zr-search-input'], isDropdownOpen && styles['zr-search-active'])}
                value={searchTerm}
                onChange={(e) => handleChange(e)}
                placeholder={placeholder}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                disabled={!searchable}
            />
            {!searchable &&
                <button
                    className={cx(styles["zr-cover-search"])}
                    onClick={() => setDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                />
            }
            <label className={cx(value !== '' && styles['zr-filled'])}>{placeholder}</label>
            <span className={cx(styles['focus-border'])}>
                <i/>
            </span>
            {(isDropdownOpen && options.length > 0)&& (
                <ul className={cx(styles['zr-dropdown'])}>
                    {filteredOptions.map((option) => (
                        <li
                            key={option.value}
                            className={cx(styles['zr-dropdown-item'])}
                            onClick={() => handleSelectOption(option)}
                        >
                            {option.label}
                        </li>
                    ))}
                    {loading &&
                        <li
                            key={'loading'}
                            className={cx(styles['zr-dropdown-item'])}
                        >
                            <Loader isLoading={loading}/>
                        </li>
                    }
                </ul>
            )}
            {value && (
                <div className={cx(styles['zr-delete-button'])} onClick={handleClearSelection}>&#x2715;</div>
            )}
        </div>
    );
};

export default Select;
